import { useCallback, useState, VoidFunctionComponent } from 'react';
import { useMount } from 'react-use';
import { BotQuizAnswer, BotQuizAttempt, BotQuizCaptchaAnswers } from '@/lib/types';
import { SusResult } from '@/components/scenes/quiz/SusResult';
import { HumanResult } from '@/components/scenes/quiz/HumanResult';
import { Captcha } from '@/components/scenes/quiz/Captcha';
import { BotQuiz } from '@/components/scenes/quiz/BotQuiz';
import { useQuizQuestions } from '@/hooks/useQuizQuestions';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { MOSAIC_TRANSITION_DURATION } from '@/lib/globals';
import { useWalletContext } from '@/context/WalletContext';
import { useEffectContext } from '@/hooks/useEffectContext';

// const RESET_AFTER_TIME = 12 * 60 * 60 * 1000;

type QuizScene = 'SUS' | 'HUMAN' | 'QUESTIONS' | 'CAPTCHA';

interface Props {
    address: string;
    onPass: () => void;
    onCancel: () => void;
}

const QUIZ_VERSION = 'v2000003'; // to invalidate previous authorizations, update this
const generateStorageKey = (address: string, key: string): string => `${address}_${key}_${QUIZ_VERSION}`;

export const Quiz: VoidFunctionComponent<Props> = ({ address, onPass, onCancel }) => {
    const { code } = useWalletContext();
    const { setPixelate } = useEffectContext();
    const [quizScene, setQuizScene] = useState<QuizScene>('SUS');
    const [answers, setAnswers] = useState<BotQuizAnswer[]>([]);
    const [firstAttempt, setFirstAttempt] = useState<boolean>(true);
    const [currentAttemptIndex, setCurrentAttemptIndex] = useLocalStorage<number>(
        generateStorageKey(address, 'botQuizCurrentAttempt'),
        0
    );
    const [attempts, setAttempts] = useState<BotQuizAttempt[]>([]);
    // const [clearAttemptsOnDismount, setClearAttemptsOnDismount] = useState<boolean>(false);
    // const [failedQuizTimestamp, setFailedQuizTimestamp] = useLocalStorage<number>(
    //     generateStorageKey(address, 'failedQuizTimetstamp'),
    //     0
    // );
    const { fetchQuiz: fetchQuizFromBackend, submitQuiz: submitQuizToBackend } = useQuizQuestions(address);

    // fetch quiz from backend
    const fetchQuiz = useCallback(async () => {
        setAttempts(await fetchQuizFromBackend());
    }, [fetchQuizFromBackend]);

    // const resetQuiz = useCallback(() => {
    //     setAttempts([]);
    //     setCurrentAttemptIndex(0);
    //     setFailedQuizTimestamp(0);
    // }, [setAttempts, setCurrentAttemptIndex, setFailedQuizTimestamp]);

    const transitionToQuizScene = useCallback(
        (nextScene: QuizScene) => {
            setPixelate(true);
            setTimeout(() => {
                setQuizScene(nextScene);
                setPixelate(false);
            }, MOSAIC_TRANSITION_DURATION * 1000);
        },
        [setPixelate]
    );

    // handle failure cases
    // useEffect(() => {
    //     if (failedQuizTimestamp > 0) {
    //         if (Date.now() - failedQuizTimestamp < RESET_AFTER_TIME) {
    //             setQuizScene('HUMAN');
    //         } else if (Date.now() - failedQuizTimestamp >= RESET_AFTER_TIME) {
    //             resetQuiz();
    //         }
    //     }
    // }, [failedQuizTimestamp, resetQuiz, transitionToQuizScene]);

    // useEffect(() => {
    //     if (clearAttemptsOnDismount) {
    //         return () => {
    //             resetQuiz();
    //         };
    //     }
    // }, [clearAttemptsOnDismount, resetQuiz]);

    // determine which questions are in current attempt
    const currentAttempt = attempts[(currentAttemptIndex ?? 0) % attempts.length];

    const submitQuiz = useCallback(
        async (arg: string | BotQuizCaptchaAnswers) => {
            const request =
                typeof arg === 'string'
                    ? arg
                    : {
                          auth: currentAttempt.auth,
                          submission: answers,
                          captcha: arg,
                      };

            let success = false;
            try {
                success = await submitQuizToBackend(request);

                // if (success) {
                //     setClearAttemptsOnDismount(true);
                //     onPass();
                // } else {
                //     if (currentAttemptIndex >= attempts.length - 1) {
                //         setFailedQuizTimestamp(Date.now());
                //     } else {
                //         setCurrentAttemptIndex((current) => current + 1);
                //         transitionToQuizScene('SUS');
                //     }
                // }
            } catch (e) {
                // if (currentAttemptIndex >= attempts.length - 1) {
                //     setFailedQuizTimestamp(Date.now());
                // } else {
                //     setCurrentAttemptIndex((current) => current + 1);
                //     transitionToQuizScene('SUS');
                // }
                console.error(e);
            }

            setFirstAttempt(false);
            setCurrentAttemptIndex((current) => current + 1);
            if (success) {
                onPass();
            } else {
                transitionToQuizScene('SUS');
            }
        },
        [
            submitQuizToBackend,
            currentAttempt,
            answers,
            onPass,
            // currentAttemptIndex,
            // attempts.length,
            // setFailedQuizTimestamp,
            setCurrentAttemptIndex,
            transitionToQuizScene,
        ]
    );

    useMount(() => {
        if (code !== null) {
            void submitQuiz(code);
        } else {
            void fetchQuiz();
        }
    });

    const submitCaptcha = useCallback(
        (answers: BotQuizCaptchaAnswers) => {
            void submitQuiz(answers);
        },
        [submitQuiz]
    );

    const transitionToBotQuiz = useCallback(() => {
        if (currentAttempt?.questions) {
            transitionToQuizScene('QUESTIONS');
        }
    }, [currentAttempt?.questions, transitionToQuizScene]);

    if (!currentAttempt) {
        return null;
    }

    switch (quizScene) {
        case 'SUS':
            return <SusResult firstAttempt={firstAttempt} beginQuiz={transitionToBotQuiz} cancel={onCancel} />;
        case 'HUMAN':
            return <HumanResult cancel={onCancel} />;
        case 'QUESTIONS':
            return (
                <BotQuiz
                    questions={currentAttempt.questions}
                    submitAnswers={(answers: BotQuizAnswer[]) => {
                        setAnswers(answers);
                        transitionToQuizScene('CAPTCHA');
                    }}
                />
            );
        case 'CAPTCHA':
            return <Captcha captcha={currentAttempt.captcha} submitCaptcha={submitCaptcha} />;
        default:
            return <SusResult firstAttempt={firstAttempt} beginQuiz={transitionToBotQuiz} cancel={onCancel} />;
    }
};
